import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";

import { updateSearchQuery } from "@dashboard/devices/store";
import type { Group } from "@dashboard/devices/types";
import { setColumnVisibility } from "@dashboard/devices/utils/columnVisibility";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getRootGroupIdsInSelection } from "@dashboard/groups/utils";
import { useCurrentWorkspace, useWorkspaceFeature } from "@dashboard/workspaces/api/helpers";
import { Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ElasticService from "@services/elasticService";
import { CustomTileContainer, CustomTileLink, CustomTileText, CustomTileTitle, HeaderContainer } from "@shared/CustomTile";
import { ProgressBar } from "@shared/ProgressBar";

export const getBarValue = (usedCount: number, total: number) => {
    if (total === Infinity) {
        return 100;
    }
    if (total === 0) {
        return 0;
    }
    return (usedCount / total) * 100;
};
export const getBarColor = (barValue: number, limit: number, theme: Theme) => {
    if (limit === Infinity) {
        return theme.palette.emteria.success;
    }
    if (barValue >= 98) {
        return theme.palette.emteria.error;
    }
    if (barValue > 80) {
        return theme.palette.emteria.warning;
    }
    if (barValue > 0) {
        return theme.palette.emteria.success;
    }
    return theme.palette.emteria.success;
};

const EnrollmentProgressCard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { t } = useTranslation(["pages", "common"]);
    const theme = useTheme();

    const { data: workspace } = useCurrentWorkspace();
    const groups: Group[] = useSelector(selectGroups);
    const deviceCount = useMemo(() => {
        const rootGroupIds = new Set(getRootGroupIdsInSelection(groups.map((g) => g.id), groups));
        return groups.reduce(
            (acc, group) => (rootGroupIds.has(group.id) ? acc + (group.statistics?.deviceCount ?? 0) : acc),
            0,
        );
    }, [groups]);
    const licenseLimit = useWorkspaceFeature("LicenseLimit");

    const barValue = getBarValue(deviceCount, licenseLimit);
    const barColor = getBarColor(barValue, licenseLimit, theme);

    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (deviceCount === licenseLimit && licenseLimit !== Infinity) {
            window.open("https://emteria.com/p/subscription", "_blank", "noopener,noreferrer");
        } else {
            const filters = [{ id: "licenseStatus", value: ["Used"] }];

            UserSettingsService.setWorkspaceValue("devicePage.columnFilters", filters);
            setColumnVisibility("licenseStatus");
            dispatch(updateSearchQuery(""));
            void navigate("/dashboard/devices");
        }
        const text = e.currentTarget.textContent;
        if (text) {
            ElasticService.createTransaction("overviewPageLinkClick", {
                tab: "fleet-size",
                text,
            });
        }
    };

    return (
        <CustomTileContainer>
            <HeaderContainer>
                <CustomTileTitle>{t("pages:overviewPage.cards.enrollmentProgress.title")}</CustomTileTitle>
            </HeaderContainer>
            <React.Fragment>
                {!deviceCount ? <CustomTileText>{t("common:table.noDevices")}</CustomTileText> : null}
                {licenseLimit === Infinity ? (
                    <CustomTileLink onClick={handleOnClick}>
                        {t("pages:overviewPage.cards.enrollmentProgress.devicesEnrolled", { count: deviceCount })}
                    </CustomTileLink>
                ) : (
                    <React.Fragment>
                        {deviceCount === licenseLimit && workspace ? (
                            <CustomTileLink onClick={handleOnClick}>
                                {t("pages:overviewPage.cards.enrollmentProgress.limitReached")}
                            </CustomTileLink>
                        ) : (
                            <CustomTileLink onClick={handleOnClick}>
                                {t("pages:overviewPage.cards.enrollmentProgress.devicesEnrolledOf", {
                                    total: deviceCount,
                                    limit: licenseLimit,
                                })}
                            </CustomTileLink>
                        )}
                    </React.Fragment>
                )}
                {deviceCount ? <ProgressBar progress={barValue} color={barColor} /> : null}
            </React.Fragment>
        </CustomTileContainer>
    );
};

export default EnrollmentProgressCard;
